import { useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Section } from "@astryxdesign/core/Section";
import { VStack } from "@astryxdesign/core/VStack";
import { HStack } from "@astryxdesign/core/HStack";
import { Text } from "@astryxdesign/core/Text";
import { Button } from "@astryxdesign/core/Button";
import { IconButton } from "@astryxdesign/core/IconButton";
import { Switch } from "@astryxdesign/core/Switch";
import { StatusDot } from "@astryxdesign/core/StatusDot";
import { Skeleton } from "@astryxdesign/core/Skeleton";
import { Table, pixel, proportional, type TableColumn } from "@astryxdesign/core/Table";
import { TextInput } from "@astryxdesign/core/TextInput";
import { AlertDialog } from "@astryxdesign/core/AlertDialog";
import { EmptyState } from "@astryxdesign/core/EmptyState";
import { useToast } from "@astryxdesign/core/Toast";
import { Plus, Pencil, Trash2, MapPin, Search } from "lucide-react";
import { type CreateProjectInput, type Project, type UpdateProjectInput, api } from "../../lib/api";
import { PageHeader } from "../../components/PageHeader";
import { useIsNarrowViewport, useResponsiveTableDensity } from "../../lib/responsive";
import { RetryBanner } from "../../components/RetryBanner";
import { ProjectFormDialog } from "./ProjectFormDialog";

const LOADING_ROW_COUNT = 3;

interface ProjectRow extends Record<string, unknown> {
  project: Project;
  cameraCount: number;
}

export function ProjectsPage() {
  const tableDensity = useResponsiveTableDensity();
  const isNarrow = useIsNarrowViewport();
  const queryClient = useQueryClient();
  const showToast = useToast();

  const {
    data: projects,
    isLoading,
    error,
    refetch,
    isRefetching,
  } = useQuery({ queryKey: ["projects"], queryFn: api.projects.list });
  const { data: cameras } = useQuery({ queryKey: ["cameras"], queryFn: api.cameras.list });

  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<Project | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Project | null>(null);
  const [search, setSearch] = useState("");

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["projects"] });

  const createMutation = useMutation({
    mutationFn: (input: CreateProjectInput) => api.projects.create(input),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Project added" });
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, input }: { id: string; input: UpdateProjectInput }) => api.projects.update(id, input),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Project updated" });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.projects.delete(id),
    onSuccess: () => {
      invalidate();
      showToast({ body: "Project removed" });
      setDeleteTarget(null);
    },
    onError: (err) => {
      showToast({ body: err instanceof Error ? err.message : "Couldn't delete project", type: "error" });
    },
  });

  const toggleMutation = useMutation({
    mutationFn: ({ project, active }: { project: Project; active: boolean }) =>
      active ? api.projects.enable(project.id, project.version) : api.projects.disable(project.id, project.version),
    onSuccess: invalidate,
    onError: (err) => {
      showToast({ body: err instanceof Error ? err.message : "Couldn't change project state", type: "error" });
    },
  });

  const cameraCountByProject = new Map<string, number>();
  for (const camera of cameras ?? []) {
    cameraCountByProject.set(camera.project_id, (cameraCountByProject.get(camera.project_id) ?? 0) + 1);
  }
  const rows: ProjectRow[] = (projects ?? []).map((project) => ({
    project,
    cameraCount: cameraCountByProject.get(project.id) ?? 0,
  }));

  // Client-side, over data that's already fully loaded — this table has no
  // pagination and never fetches a second page, so there's nothing a server
  // search would save. Matches the same fields the row itself shows: name,
  // code (how a site is actually referred to over the radio), and location.
  const searchLower = search.trim().toLowerCase();
  const filteredRows = searchLower
    ? rows.filter(
        ({ project }) =>
          project.name.toLowerCase().includes(searchLower) ||
          project.code.toLowerCase().includes(searchLower) ||
          project.location.toLowerCase().includes(searchLower),
      )
    : rows;

  const openAddDialog = () => {
    setEditing(null);
    setFormOpen(true);
  };

  // Dropped whole on a phone rather than squeezed: the code, camera count and
  // timezone are all short facts that read fine on one folded line inside the
  // name cell, and three columns of chrome to carry them is what pushed the
  // Active switch and the edit/delete buttons off the right edge.
  const detailColumns: TableColumn<ProjectRow>[] = [
    {
      key: "code",
      header: "Code",
      width: pixel(100),
      renderCell: ({ project }) => <Text type="code">{project.code}</Text>,
    },
    {
      key: "timezone",
      header: "Timezone",
      width: pixel(160),
      renderCell: ({ project }) => (
        <Text type="supporting" color="secondary">
          {project.timezone}
        </Text>
      ),
    },
    {
      key: "cameras",
      header: "Cameras",
      width: pixel(100),
      renderCell: ({ cameraCount }) => <Text type="supporting">{cameraCount}</Text>,
    },
  ];

  let body: ReactNode;
  if (isLoading) {
    body = (
      <VStack gap={2}>
        {Array.from({ length: LOADING_ROW_COUNT }, (_, i) => (
          // eslint-disable-next-line react/no-array-index-key -- fixed-count static placeholders, never reordered
          <Skeleton key={i} height={44} radius={2} index={i} />
        ))}
      </VStack>
    );
  } else if (error) {
    body = (
      <RetryBanner
        title="Couldn't load projects"
        description={error instanceof Error ? error.message : "Unknown error"}
        onRetry={() => void refetch()}
        isRetrying={isRefetching}
      />
    );
  } else if (rows.length === 0) {
    body = (
      <EmptyState
        icon={<MapPin size={32} />}
        title="No projects yet"
        description="Add your first project (site) before adding cameras — every camera belongs to one."
        actions={<Button label="Add project" variant="primary" icon={<Plus />} onClick={openAddDialog} />}
      />
    );
  } else {
    body = (
      <VStack gap={3}>
        <TextInput
          label="Search projects"
          isLabelHidden
          placeholder="Search by name, code, or location"
          value={search}
          onChange={setSearch}
          startIcon={<Search size={16} />}
          hasClear
        />
        {filteredRows.length === 0 ? (
          <EmptyState
            icon={<Search size={32} />}
            title="No matching projects"
            description={`No project matches "${search}".`}
          />
        ) : (
          <Table<ProjectRow>
            data={filteredRows}
            idKey={(row) => row.project.id}
            density={tableDensity}
            hasHover
            dividers="rows"
            columns={[
              ...(isNarrow ? [] : detailColumns),
              {
                key: "name",
                header: "Project",
                width: proportional(2),
                renderCell: ({ project, cameraCount }) => (
                  <HStack gap={1.5} vAlign="center">
                    <StatusDot variant={project.is_active ? "success" : "neutral"} label={project.is_active ? "Active" : "Inactive"} />
                    <VStack gap={0}>
                      <Text weight="medium">{project.name}</Text>
                      {project.location && (
                        <Text type="supporting" color="secondary">
                          {project.location}
                        </Text>
                      )}
                      {/* Stands in for the three columns dropped on a phone.
                          The project code is how a site is referred to over
                          the radio, so losing it entirely would make this
                          table useless for the person actually on site. */}
                      {isNarrow && (
                        <Text type="supporting" color="secondary">
                          {project.code} · {cameraCount} camera{cameraCount === 1 ? "" : "s"} · {project.timezone}
                        </Text>
                      )}
                    </VStack>
                  </HStack>
                ),
              },
              {
                key: "active",
                header: "Active",
                width: pixel(90),
                renderCell: ({ project }) => (
                  <Switch
                    label={`${project.is_active ? "Disable" : "Enable"} ${project.name}`}
                    isLabelHidden
                    value={project.is_active}
                    changeAction={async (active) => {
                      await toggleMutation.mutateAsync({ project, active });
                    }}
                  />
                ),
              },
              {
                key: "actions",
                header: "",
                width: pixel(96),
                renderCell: ({ project }) => (
                  <HStack gap={1} justify="end">
                    <IconButton
                      label={`Edit ${project.name}`}
                      tooltip="Edit"
                      icon={<Pencil />}
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setEditing(project);
                        setFormOpen(true);
                      }}
                    />
                    <IconButton
                      label={`Delete ${project.name}`}
                      tooltip="Delete"
                      icon={<Trash2 />}
                      variant="ghost"
                      size="sm"
                      onClick={() => setDeleteTarget(project)}
                    />
                  </HStack>
                ),
              },
            ]}
          />
        )}
      </VStack>
    );
  }

  return (
    <Section padding={4}>
      <VStack gap={4}>
        <PageHeader
          title="Projects"
          description={`${rows.length} project${rows.length === 1 ? "" : "s"} (sites) · every camera belongs to one`}
          actions={<Button label="Add project" variant="primary" icon={<Plus />} onClick={openAddDialog} />}
        />
        {body}
      </VStack>

      <ProjectFormDialog
        isOpen={formOpen}
        onOpenChange={setFormOpen}
        editing={editing}
        isSaving={createMutation.isPending || updateMutation.isPending}
        onCreate={(input) => createMutation.mutateAsync(input)}
        onUpdate={(id, input) => updateMutation.mutateAsync({ id, input })}
      />

      <AlertDialog
        isOpen={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        title={`Delete ${deleteTarget?.name ?? "this project"}?`}
        description="Projects that still have cameras assigned can't be deleted — move or remove those cameras first. This cannot be undone."
        actionLabel="Delete project"
        isActionLoading={deleteMutation.isPending}
        onAction={() => deleteTarget && deleteMutation.mutate(deleteTarget.id)}
      />
    </Section>
  );
}
